import { adminApiClient } from '@/services/admin/baseService'
import type { ApiResponse, PaginatedResponse, RescueTask, RescueRunResult } from '@/types'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import {
  buildPaginatedResponse,
  extractArrayFromApiPayload,
} from '@/services/admin/paginatedResponseHelpers'

const normalizePaginatedResponse = <T>(
  payload: ApiResponse<any> | undefined,
  options: { keys?: string[]; defaultPage?: number; defaultLimit?: number } = {}
): PaginatedResponse<T> => {
  const payloadAny = payload as any
  const items = extractArrayFromApiPayload<T>(payloadAny, {
    keys: options.keys,
    candidateKeys: ['items', 'records', 'tasks'],
  })
  return buildPaginatedResponse(payload, items, options)
}

export interface RescueTaskListParams {
  page?: number
  limit?: number
  status?: string
  checkKey?: string
  triggerSource?: string
  targetType?: string
  chainId?: string
  merchantId?: string
  runId?: string
  targetId?: string
  search?: string
}

export interface RescueRunParams {
  checkKeys?: string[]
  chainIds?: string[]
  merchantId?: string
  orderIds?: string[]
  limit?: number
  dryRun?: boolean
}

export interface SolanaAddressRecoveryContext {
  orderId: string
  orderBusinessId?: string | null
  paymentToken?: string | null
  network: string
  tokenMint?: string | null
  paymentAddress?: string | null
  vaultTokenAccount?: string | null
  nestedTokenAccount?: string | null
  vaultBalance?: string | null
  nestedBalance?: string | null
  orderStatus?: string | null
  recoveryEligible: boolean
  recoveryBlockedReason?: string | null
  latestTaskStatus?: string | null
  latestTxHash?: string | null
}

export interface SolanaAddressRecoveryRunParams {
  orderIdOrBusinessId: string
  dryRun?: boolean
  note?: string
}

export interface SolanaAddressRecoveryRunResult {
  dryRun: boolean
  taskCreated: boolean
  eligible: boolean
  blockedReason?: string | null
  resolvedOrderId: string
  sourceIdentifier: string
  sourceIdentifierType: string
  network: string
  paymentAddress?: string | null
  vaultTokenAccount?: string | null
  nestedTokenAccount?: string | null
  vaultBalanceBefore?: string | null
  nestedBalanceBefore?: string | null
  wouldRecoverAmount?: string | null
}

export interface ExpiredPaidOrderRepairRunParams {
  orderIdOrBusinessId: string
  transactionHash?: string
  transactionHashes?: string[]
  dryRun?: boolean
  note?: string
}

export interface ExpiredPaidOrderRepairRunResult {
  dryRun: boolean
  mode: string
  outcome: 'eligible' | 'blocked' | 'applied' | 'applied_with_warnings' | 'failed'
  orderId?: string
  orderBusinessId?: string | null
  sourceIdentifier: string
  sourceIdentifierType?: 'internal_id' | 'business_id'
  transactionHash: string
  transactionHashes?: string[]
  chainId?: string | null
  chainCode?: string | null
  chainCompatibility?: string | null
  tokenAddress?: string | null
  tokenDecimals?: number | null
  chainNumericId?: number | null
  publicPaymentAddress?: string | null
  operationalAddress?: string | null
  operationalAddressType?: 'contract' | 'vault_token_account'
  paymentAddress?: string | null
  currentStatus?: string | null
  expectedAmountInSmallestUnit?: string | null
  existingPaidAmountInSmallestUnit?: string | null
  matchedAmountInSmallestUnit?: string | null
  finalPaidAmountInSmallestUnit?: string | null
  oldOnchainBalanceInSmallestUnit?: string | null
  newOnchainBalanceInSmallestUnit?: string | null
  insertedPaymentLogCount?: number
  targetStatus?: string | null
  blockedReason?: string | null
  warnings?: string[]
  sideEffectWarnings?: string[]
  appliedAt?: string | null
  matchedTransfers?: Array<{
    transactionHash: string
    logIndex: number
    blockNumber: number
    fromAddress: string
    toAddress: string
    tokenAddress: string
    amountInSmallestUnit: string
    alreadyLogged: boolean
    paymentLogId?: string | null
  }>
  rejectedTransactions?: Array<{
    transactionHash: string
    reason: string
  }>
  transactionResults?: Array<{
    transactionHash: string
    status: 'matched' | 'rejected' | 'not_evaluated'
    matchedAmountInSmallestUnit: string
    matchedTransferCount: number
    alreadyLoggedCount: number
    paymentLogIds: string[]
    rejectedReason?: string | null
  }>
}

export const rescueService = {
  async getRescueTasks(
    params?: RescueTaskListParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<RescueTask>> {
    const searchParams = new URLSearchParams()

    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.status) searchParams.append('status', params.status)
    if (params?.checkKey) searchParams.append('checkKey', params.checkKey)
    if (params?.triggerSource) searchParams.append('triggerSource', params.triggerSource)
    if (params?.targetType) searchParams.append('targetType', params.targetType)
    if (params?.chainId) searchParams.append('chainId', params.chainId)
    if (params?.merchantId) searchParams.append('merchantId', params.merchantId)
    if (params?.runId) searchParams.append('runId', params.runId)
    if (params?.targetId) searchParams.append('targetId', params.targetId)
    if (params?.search) searchParams.append('search', params.search)

    const query = searchParams.toString()
    const url = query
      ? `/admin/rescue/fix-payment-log/tasks?${query}`
      : '/admin/rescue/fix-payment-log/tasks'
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))
    return normalizePaginatedResponse<RescueTask>(response.data, {
      keys: ['tasks'],
      defaultPage: params?.page,
      defaultLimit: params?.limit,
    })
  },

  async runRescue(
    params?: RescueRunParams,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<RescueRunResult>> {
    const response = await adminApiClient.post<ApiResponse<RescueRunResult>>(
      '/admin/rescue/fix-payment-log/run',
      params ?? {},
      buildModeAwareConfig(mode)
    )
    return response.data
  },

  async inspectSolanaAddressRecoveryOrder(
    orderIdOrBusinessId: string,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<SolanaAddressRecoveryContext>> {
    const response = await adminApiClient.get<ApiResponse<SolanaAddressRecoveryContext>>(
      `/admin/rescue/solana-address-recovery/orders/${encodeURIComponent(orderIdOrBusinessId)}`,
      buildModeAwareConfig(mode)
    )
    return response.data
  },

  async runSolanaAddressRecovery(
    params: SolanaAddressRecoveryRunParams,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<SolanaAddressRecoveryRunResult>> {
    const response = await adminApiClient.post<ApiResponse<SolanaAddressRecoveryRunResult>>(
      '/admin/rescue/solana-address-recovery/run',
      params,
      buildModeAwareConfig(mode)
    )
    return response.data
  },

  async runExpiredPaidOrderRepair(
    params: ExpiredPaidOrderRepairRunParams,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<ExpiredPaidOrderRepairRunResult>> {
    const response = await adminApiClient.post<ApiResponse<ExpiredPaidOrderRepairRunResult>>(
      '/admin/rescue/expired-paid-orders/run',
      params,
      buildModeAwareConfig(mode)
    )
    return response.data
  },
}
